import type { BasketModel } from "~/commerce-api/models/basket";
import { type HecProduct, isLMNProduct } from "~/hec/types/product";
import {
  getMinicartSubtotal,
  getOrderTotal,
} from "~/routes/checkout/utils/totals";
import { resolvePriceFromHecProduct } from "~/shop/utils/price-display";

/**
 * FSA funds maths (COM-244 / COM-252 / COM-487), in integer cents, so two-decimal
 * amounts compare exactly: the legacy float defect (79.99 - 59.99 =
 * 19.999999999999993 hiding a $20.00 item) cannot happen.
 */

export type BalanceParse =
  | { ok: true; cents: number }
  | { ok: false; error: "empty" | "negative" | "invalid" };

// parseFloat-shaped, but the whole string must be the number and at most cents:
// "5abc", "0x10", "4 0", "Infinity" and "40.999" fail; "1e3", "40." and "+40" pass.
const AMOUNT = /^\+?(\d+(\.\d{0,2})?|\.\d{1,2}|(\d+\.?\d*|\.\d+)e\+?\d+)$/i;

const toCents = (dollars: number): number => Math.round(dollars * 100);

/**
 * Legacy parse (strip "$" and ",", then trim). The only cap is exactness:
 * cents past Number.MAX_SAFE_INTEGER are invalid, since they would not compare
 * exactly or persist.
 */
export function parseBalanceInput(text: string): BalanceParse {
  const s = text.replace(/[$,]/g, "").trim();
  if (!s) return { ok: false, error: "empty" };
  if (s.startsWith("-") && AMOUNT.test(s.slice(1))) {
    return { ok: false, error: "negative" };
  }
  const cents = toCents(Number(s));
  if (!AMOUNT.test(s) || !Number.isSafeInteger(cents)) {
    return { ok: false, error: "invalid" };
  }
  return { ok: true, cents };
}

/** Balance minus cart. Negative on purpose: the display and `withinFunds` clamp at 0. */
export const remainingCents = (balance: number, cart: number): number =>
  balance - cart;

/**
 * Spent share of the balance. Red (`over`) from exactly 100%; 0/0 is not over.
 * Unlike `computeBalanceUsage` (0% at balance 0), a $0 balance with a cart is red.
 */
export function barState(
  balance: number,
  cart: number,
): { pct: number; over: boolean } {
  if (balance <= 0) {
    return cart > 0 ? { pct: 100, over: true } : { pct: 0, over: false };
  }
  return {
    pct: Math.min(100, Math.max(0, (cart / balance) * 100)),
    over: cart >= balance,
  };
}

/**
 * The price the tile renders (sale price, range minimum, bundle sales price),
 * or null when it renders no number: an LMN tile shows no price, and a set
 * with `c_plpSetPrice` shows only that text.
 */
export function paidPriceCents(product: HecProduct): number | null {
  if (isLMNProduct(product)) return null;
  const display = resolvePriceFromHecProduct(product);
  if (!display || display.setPriceText) return null;
  return toCents(display.price);
}

/**
 * Affordable items (price <= remaining), in list order; if none is affordable,
 * the whole list, cheapest first. An in-cart item over remaining drops out like
 * any other, as legacy does (SD-30); the ribbon still lists it. Pass the whole
 * chip, not a display page: the fallback needs it (why not CIO's price filter:
 * docs/fsa-deadline-pages.md §1).
 */
export function withinFunds<P extends HecProduct>(
  list: readonly P[],
  remaining: number,
): P[] {
  const affordable = (p: P) => {
    const cents = paidPriceCents(p);
    return cents !== null && cents <= Math.max(0, remaining);
  };
  if (list.some(affordable)) {
    return list.filter(affordable);
  }
  return (
    list
      .map(p => ({ p, cents: paidPriceCents(p) ?? Infinity }))
      // Infinity - Infinity is NaN, which sort() treats as a tie (stable).
      .sort((a, b) => a.cents - b.cents)
      .map(({ p }) => p)
  );
}

/**
 * The cart figure: the header minicart's "Estimated Total"
 * (`getMinicartSubtotal`), so the ribbon shows the same number for the same
 * cart (COM-721). SFCC-10084 took shipping out of the minicart figure; this
 * follows it (COM-883), so remaining funds no longer count shipping either.
 */
export function estimatedTotalCents(basket: BasketModel): number {
  return toCents(getMinicartSubtotal(basket));
}

/**
 * The order total the TPA balance tracker's server figure uses (`getOrderTotal`:
 * shipping in, gift card and Annex rewards netted). Its reload trigger must move
 * with that figure, not the minicart's, or a shipping or reward change leaves
 * "Left to spend" stale (COM-883).
 */
export function orderTotalCents(basket: BasketModel): number {
  return toCents(getOrderTotal(basket));
}
